/**
 * TemplatesMetaPage — Brief 4 (Gestor) · Tela 3 Templates Meta WhatsApp / HSM
 * (ADR-127 + ADR-054 D-6 · PR1 Foundation + leitura).
 *
 * Rota: /admin/integracoes/templates-meta. Leitura sobre o CRUD real de templates Meta.
 * Padrão D-B (defense-in-depth): gate próprio na página (`message-template-read`),
 * além da query gateada no hook — não confia só na rota/nav.
 *
 * Escopo PR1: lista → editor shell em leitura → preview WhatsApp. Criar/editar/
 * submeter à Meta/toggle D-6 (gate `message-template-manage`) chegam no PR2.
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useState } from 'react';

import { TemplateCreateModal } from '../features/message-templates/TemplateCreateModal';
import { TemplateEditorView } from '../features/message-templates/TemplateEditorView';
import { TemplateListView } from '../features/message-templates/TemplateListView';
import { useTemplate, useTemplateList } from '../features/message-templates/use-templates';
import { useCapability } from '../hooks/use-capability';
import { useAuthStore } from '../stores/auth-store';

import '../features/message-templates/message-templates.css';

export function TemplatesMetaPage(): JSX.Element {
  const canRead = useCapability('message-template-read');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canRead) {
    return (
      <div className="message-templates-screen message-templates-screen--gate">
        <CapabilityLockedBanner
          capability="message-template-read"
          tierMin="professional"
          fallbackMessage="A gestão de templates Meta é restrita a perfis de gestão — fale com o administrador do tenant."
        />
      </div>
    );
  }

  return <TemplatesMetaPageInner />;
}

function TemplatesMetaPageInner(): JSX.Element {
  const canManage = useCapability('message-template-manage');
  const brand = useAuthStore((s) => s.tenant?.name ?? 'Sua empresa');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const list = useTemplateList();
  const detail = useTemplate(selectedId);

  if (selectedId !== null) {
    if (detail.isLoading) {
      return (
        <div className="message-templates-screen">
          <div className="mt-page">
            <p className="mt-fg3">Carregando template…</p>
          </div>
        </div>
      );
    }
    if (detail.isError || detail.data === undefined) {
      return (
        <div className="message-templates-screen">
          <div className="mt-page">
            <p className="mt-banner mt-banner--danger" role="alert">
              Não foi possível carregar este template.
            </p>
            <button type="button" className="mt-btn" onClick={() => setSelectedId(null)}>
              Voltar à lista
            </button>
          </div>
        </div>
      );
    }
    return (
      <TemplateEditorView
        key={`${detail.data.id}:${detail.data.version.toString()}`}
        template={detail.data}
        brand={brand}
        canManage={canManage}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  if (list.isLoading) {
    return (
      <div className="message-templates-screen">
        <div className="mt-page">
          <p className="mt-fg3">Carregando templates…</p>
        </div>
      </div>
    );
  }
  if (list.isError) {
    return (
      <div className="message-templates-screen">
        <div className="mt-page">
          <p className="mt-banner mt-banner--danger" role="alert">
            Falha ao carregar os templates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <TemplateListView
        templates={list.data}
        canManage={canManage}
        onOpen={(id) => setSelectedId(id)}
        onCreate={() => setCreating(true)}
      />
      {creating && (
        <TemplateCreateModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setSelectedId(id);
          }}
        />
      )}
    </>
  );
}
